import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Table, { ITableColumn } from '@/component/table/Table.tsx'
import Pagination from '@/component/pagination/Pagination.tsx'
import useQueryGetNotificationBio, { IAlertBio } from '@/api/notification/getNotificationBio.ts'
import { IAlertEmergency } from '@/api/notification/getNotificationSos.ts'
import { format } from 'date-fns'
import { equalString } from '@/util/strings.ts'
import AlertConfirmStatusBadge from '@/component/badge/AlertConfirmStatusBadge.tsx'
import BioAlertModal from '@/pages/notification/bio/BioAlertModal.tsx'
import SearchBar from '@/component/input/SearchBar.tsx'
import useDebounce from '@/util/hook/useDebounce.tsx'
import ExcelDownloadIcon from '@/assets/ic_notification_sos_excel_download.svg?react'
import { useTranslation } from 'react-i18next'
import Tooltip from '@/component/tooltip/Tooltip.tsx'
import Input from '@/component/input/Input.tsx'
import SolidButton from '@/component/button/SolidButton.tsx'
import { createUrl } from '@/util/url.ts'
import { formatAlertLocation } from '@/util/alertLocation.ts'
import { getAlertGenderName, getAlertResultName } from '@/util/alertDisplay.ts'
import { AlertVitalStatus } from '@/api/notification/alertServerValues.ts'
import { apiDownload } from '@/api/api.ts'
import { useAuth } from '@/hooks/useAuth'
import useQueryGetMe from '@/api/auth/getMe.ts'
import {
    CURRENT_YEAR,
    EARLIEST_EXPORT_YEAR,
    isValidExportPeriod,
    normalizeExportMonth,
    normalizeExportYear
} from '@/util/excelPeriod.ts'

export interface IProps {
    className?: string
}

const PAGE_SIZE = 10

const NotificationBioContainer: React.FC<IProps> = ({ className }) => {
    const { t } = useTranslation()
    const { data: me } = useQueryGetMe()
    const { isLoggedIn } = useAuth()

    const [currentPage, setCurrentPage] = useState(1)
    const [search, setSearch] = useState('')
    const [year, setYear] = useState(String(CURRENT_YEAR))
    const [month, setMonth] = useState('')
    const [detailModalId, setDetailModalId] = useState<number>(-1)
    const detailModalRef = useRef<any>()
    const debouncedSearchText = useDebounce(search, 200)

    // 특정 이메일 계정인지 확인
    const isMaskingRequired = useMemo(() => {
        return me?.email === 'dbd_cm@clomental.com'
    }, [me?.email])

    // 마스킹 처리 함수
    const getMaskedName = (name?: string) => {
        if (!isMaskingRequired || !name) return name
        if (name.length <= 1) return name
        return name.charAt(0) + '**'
    }

    const getMaskedContact = (contact?: string) => {
        if (!isMaskingRequired || !contact) return contact

        // 모든 전화번호 앞 3자리만 보여주고 나머지는 * 처리
        const contactParts = contact?.split('-')
        if (contactParts?.length === 3) {
            return `${contactParts[0]}-****-****`
        }

        // 기타 패턴의 전화번호 처리
        if (contact?.length > 3) {
            return contact.substring(0, 3) + contact.substring(3).replace(/./g, '*')
        }

        return contact
    }

    const getMaskedAddress = (address?: string) => {
        if (!isMaskingRequired || !address) return address
        return '****'
    }

    useEffect(() => {
        setCurrentPage(1)
    }, [debouncedSearchText])

    const { data, refetch } = useQueryGetNotificationBio({
        pageNum: currentPage,
        pageSize: PAGE_SIZE
    }, debouncedSearchText)

    const getTitle = useCallback(() => {
        return t('notification.bio.title')
    }, [t])

    useEffect(() => {
        refetch()
    }, [currentPage])

    const getGenderName = useCallback((value: string) => getAlertGenderName(t, 'bioTable', value), [t])

    const getResultName = useCallback((value: string) => getAlertResultName(t, 'bioTable', value), [t])

    const columns = useMemo<ITableColumn<IAlertBio>[]>(() => {
        const result: ITableColumn<IAlertBio>[] = [
            {
                key: 'alert_time',
                header: () => <p
                    className={'font-medium text-[14px]'}>{t('notification.bio.table.emergency.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertBio) => {
                    return <p className={'text-center'}>{format(item?.alert?.createdAt, 'yyyy/MM/dd HH:mm')}</p>
                }
            },
            {
                key: 'checked_time',
                header: () => <p
                    className={'font-medium text-[14px]'}>{t('notification.bio.table.confirmed.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertBio) => {
                    return <p
                        className={'text-center'}>{item?.alert?.checkedAt && format(item?.alert?.checkedAt, 'yyyy/MM/dd HH:mm')}</p>
                }
            },
            {
                key: 'manager_name',
                header: () => <p className={'font-medium text-[14px]'}>{t('notification.bio.table.manager.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertBio) => {
                    return <p
                        className={'text-center'}>{getMaskedName(item?.alert?.managerName)}</p>
                }
            },
            {
                key: 'protectee_name',
                header: () => <p
                    className={'font-medium text-[14px]'}>{t('notification.bio.table.userName.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertBio) => {
                    return <p
                        className={'text-center'}>{getMaskedName(item?.protectee?.name)}</p>
                }
            },
            {
                key: 'protectee_gender_birthday',
                header: () => <p
                    className={'font-medium text-[14px]'}>{t('notification.bio.table.gender.column')} / {t('notification.bio.table.birthday.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertBio) => {
                    return <p
                        className={'text-center'}>{getGenderName(item?.protectee?.gender)} / {isMaskingRequired ? '**-**-**' : format(item?.protectee?.birthDate, 'yy-MM-dd')}</p>
                }
            },
            {
                key: 'protectee_contact',
                header: () => <p className={'font-medium text-[14px]'}>{t('notification.bio.table.contact.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertBio) => {
                    return <p
                        className={'text-center'}>{getMaskedContact(item?.protectee?.contact)}</p>
                }
            },
            {
                key: 'alert_location',
                header: () => <p className={'font-medium text-[14px]'}>{t('notification.bio.table.address.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertBio) => {
                    return <p
                        className={'text-center'}>{formatAlertLocation(item?.alert?.location, t, getMaskedAddress)}</p>
                }
            },
            {
                key: 'state',
                header: () => <p className={'font-medium text-[14px]'}>{t('notification.bio.table.state.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertBio) => {
                    const { hrate, hrateStatus, resp, respStatus } = item?.alert?.nameDetail || {}

                    const heartRateElement = hrate !== undefined ? (
                        <span className={equalString(hrateStatus, AlertVitalStatus.NORMAL) ? 'text-main_b' : 'text-sub_r'}>
                            {t('notification.bio.table.state.heartRate')}[{hrate}]
                        </span>
                    ) : null

                    const respElement = resp !== undefined ? (
                        <span className={resp >= 22 || !equalString(respStatus, AlertVitalStatus.NORMAL) ? 'text-sub_r' : 'text-main_b'}>
                            {t('notification.bio.table.state.respiration')}[{resp}]
                        </span>
                    ) : null

                    return (
                        <p className={'text-center'}>
                            {heartRateElement}
                            {heartRateElement && respElement ? ' / ' : ''}
                            {respElement}
                        </p>
                    )
                }
            },
            {
                key: 'status',
                header: () => <p className={'font-medium text-[14px]'}>{t('notification.bio.table.status.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertBio) => {
                    return <div className={'flex justify-center items-center'}><AlertConfirmStatusBadge
                        value={item?.alert?.confirm} /></div>
                }
            },
            {
                key: 'alert_type',
                header: () => <p className={'font-medium text-[14px]'}>{t('notification.bio.table.process.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertBio) => {
                    return <p
                        className={'text-center'}>{getResultName(item?.alert?.result)}</p>
                }
            }
        ]
        return result
    }, [isMaskingRequired, t])

    const renderMobileRow = (label: string, value?: React.ReactNode, valueClassName = '') => (
        <div className={'grid grid-cols-[86px_minmax(0,1fr)] gap-[8px] text-[13px]'}>
            <span className={'font-medium text-gray3'}>{label}</span>
            <span className={`min-w-0 font-medium text-gray4 ${valueClassName}`}>
                {value || '-'}
            </span>
        </div>
    )

    const renderMobileAlert = (item: IAlertBio) => {
        const { hrate, hrateStatus, resp, respStatus } = item?.alert?.nameDetail || {}
        const alertTime = item?.alert?.createdAt
            ? format(item.alert.createdAt, 'yyyy/MM/dd HH:mm')
            : '-'
        const checkedTime = item?.alert?.checkedAt
            ? format(item.alert.checkedAt, 'yyyy/MM/dd HH:mm')
            : '-'
        const birthDate = isMaskingRequired
            ? '**-**-**'
            : item?.protectee?.birthDate
              ? format(item.protectee.birthDate, 'yy-MM-dd')
              : '-'
        const heartRateElement = hrate !== undefined ? (
            <span className={equalString(hrateStatus, AlertVitalStatus.NORMAL) ? 'text-main_b' : 'text-sub_r'}>
                {t('notification.bio.table.state.heartRate')}[{hrate}]
            </span>
        ) : null
        const respirationElement = resp !== undefined ? (
            <span className={resp >= 22 || !equalString(respStatus, AlertVitalStatus.NORMAL) ? 'text-sub_r' : 'text-main_b'}>
                {t('notification.bio.table.state.respiration')}[{resp}]
            </span>
        ) : null
        const stateValue = heartRateElement || respirationElement ? (
            <>
                {heartRateElement}
                {heartRateElement && respirationElement ? ' / ' : ''}
                {respirationElement}
            </>
        ) : undefined

        return (
            <button
                className={'w-full overflow-hidden rounded-[8px] border border-solid border-gray2 bg-white text-left shadow-sm transition-colors hover:border-main_b'}
                onClick={() => {
                    setDetailModalId(item?.id)
                    detailModalRef.current?.show()
                }}
            >
                <div className={'border-b border-solid border-gray2 bg-[#F0F5FF] px-[14px] py-[12px]'}>
                    <div className={'flex items-start justify-between gap-[12px]'}>
                        <div className={'min-w-0'}>
                            <p className={'text-[12px] font-medium text-gray3'}>
                                {t('notification.bio.table.emergency.column')}
                            </p>
                            <p className={'mt-[3px] text-[14px] font-bold text-gray4'}>
                                {alertTime}
                            </p>
                        </div>
                        <div className={'flex-none'}>
                            <AlertConfirmStatusBadge value={item?.alert?.confirm} />
                        </div>
                    </div>
                </div>
                <div className={'grid gap-[9px] px-[14px] py-[12px]'}>
                    {renderMobileRow(
                        t('notification.bio.table.userName.column'),
                        getMaskedName(item?.protectee?.name)
                    )}
                    {renderMobileRow(
                        `${t('notification.bio.table.gender.column')} / ${t('notification.bio.table.birthday.column')}`,
                        `${getGenderName(item?.protectee?.gender)} / ${birthDate}`
                    )}
                    {renderMobileRow(
                        t('notification.bio.table.contact.column'),
                        getMaskedContact(item?.protectee?.contact)
                    )}
                    {renderMobileRow(
                        t('notification.bio.table.address.column'),
                        formatAlertLocation(item?.alert?.location, t, getMaskedAddress),
                        'whitespace-normal break-words'
                    )}
                    {renderMobileRow(
                        t('notification.bio.table.state.column'),
                        stateValue
                    )}
                    {renderMobileRow(
                        t('notification.bio.table.confirmed.column'),
                        checkedTime
                    )}
                    {renderMobileRow(
                        t('notification.bio.table.manager.column'),
                        getMaskedName(item?.alert?.managerName)
                    )}
                    {renderMobileRow(
                        t('notification.bio.table.process.column'),
                        getResultName(item?.alert?.result)
                    )}
                </div>
            </button>
        )
    }

    return <>
        <div className={`${className} p-[20px] sm:p-[12px]`}>
            <div className="app-toolbar">
                <div>
                    <h2 className="text-[18px] font-extrabold text-[#1C2C43]">{getTitle()}</h2>
                    <p className="mt-[3px] text-[12px] text-[#7A8799]">{t('notification.v2.bio.totalCount', { count: data?.pagination?.totalEntries || data?.data?.length || 0 })}</p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-[12px] md:justify-between sm:flex-col sm:items-stretch">
                    <Tooltip
                        render={<div className={'flex items-center font-medium text-main_b gap-[6px]'} title={t('notification.v2.excelDownload')}>
                            <span>{t('notification.bio.excel')}</span>
                            <ExcelDownloadIcon />
                        </div>}>
                        <p className={'font-bold'}>{t('notification.bio.excelModal.title')}</p>
                        <div className={'flex flex-wrap items-center gap-[7px] py-[10px]'}>
                            <Input inputType={'number'} className={'w-[96px] flex-none'}
                                value={year}
                                min={EARLIEST_EXPORT_YEAR}
                                max={CURRENT_YEAR}
                                onChanged={text => setYear(normalizeExportYear(text))} />
                            {t('notification.bio.excelModal.year')} <Input
                                inputType={'number'}
                                className={'w-[72px] flex-none'}
                                value={month}
                                min={1}
                                max={12}
                                onChanged={text => setMonth(normalizeExportMonth(text))} />{t('notification.bio.excelModal.month')}
                            <SolidButton text={t('notification.bio.excelModal.download')} className={'ml-[8px] sm:ml-0'}
                                disabled={!isValidExportPeriod(year, month)}
                                onClick={async () => {
                                    if (!isLoggedIn) return
                                    const url = createUrl(`/alert/bio/excel`, {
                                        year: year,
                                        month: month
                                    });
                                    const urlBlob = await apiDownload(url);

                                    const a = document.createElement('a')
                                    a.style.display = 'none'
                                    const objectUrl = window.URL.createObjectURL(urlBlob)
                                    a.href = objectUrl
                                    a.download = `${t('notification.v2.excelFileName.bio')}_${year}_${month}.xlsx`
                                    document.body.appendChild(a)
                                    a.click()
                                    document.body.removeChild(a)
                                    window.URL.revokeObjectURL(objectUrl)
                                }} />
                        </div>
                    </Tooltip>
                    <SearchBar inputParams={{ placeholder: t('notification.bio.search.placeholder') }}
                        className={'max-w-[230px] md:max-w-none md:w-full'} onChanged={text => {
                            setSearch(text)
                        }} />
                </div>
            </div>
            <Table
                data={data?.data || []} columns={columns} extractKey={(item) => item?.id}
                renderEmpty={<div className={'flex items-center justify-center h-full min-h-[200px]'}><p
                    className={'text-center'}>{t('notification.bio.table.empty')}</p></div>}
                className={'mt-[16px] text-[14px]'}
                itemRowClassName={() => 'border-b border-solid border-gray2'}
                itemCommonClassName={() => 'py-[10px] px-[22px]'}
                renderMobileItem={renderMobileAlert}
                onClickRow={(item: IAlertEmergency) => {
                    setDetailModalId(item?.id)
                    detailModalRef.current?.show()
                }}
            />
            {(data?.pagination?.totalPages || 0) > 0 && <Pagination
                className={'mt-[22px]'}
                current_page={data?.pagination?.currentPage}
                page_size={PAGE_SIZE}
                total_page={data?.pagination?.totalPages}
                fetchPage={(pageIndex) => {
                    setCurrentPage(pageIndex)
                }} />}
        </div>
        <BioAlertModal ref={detailModalRef} defaultId={detailModalId} refetchAlerts={refetch} />
    </>
}

export default NotificationBioContainer
